import React, { useState } from 'react';
import { CenteredContainer, LoginWrap } from '../styledComponents';
import JeppesenHeader from './JeppesenHeader';
import Alerts, { AlertType, TowerAlert } from '../../../common/Alerts';
import Typography from '@mui/material/Typography';
import { ControlBox } from '../FormController';
import { Stack } from '@mui/material';
import Footer from '../Footer';
import { useUsername } from '../../slices/hooks/useUsersSlice';
import useJdmPasswordReset from '../../slices/hooks/useJdmPasswordReset';
import ForgotPassword from '../PasswordReset/ForgotPassword';
import Button from '@mui/material/Button';
import VerifyCode from '../PasswordReset/VerifyCode';
import ResetForm from '../PasswordReset/ResetForm';
import SyncWarning from './SyncWarning';
import PasswordResetSuccess from './PasswordResetSuccess';
import JeppesenForeFlightHeader from "./JeppesenForeFlightHeader";

const codeOnLoad: string = window.CODE || '';

const PasswordResetJdm = () => {
    const { username: email, updateUsername: updateEmail } = useUsername();
    const [code, setCode] = useState<string>(codeOnLoad);
    const [alerts, setAlerts] = useState<TowerAlert[]>([]);
    const [userConfirmedUnsync, setUserConfirmedUnsync] = useState(false);

    const {
        forgotPassword,
        verifyContactCode,
        resetPassword,
        alerts: passwordAlerts,
        isLoading,
        isSuccess,
        hasTriggeredForgotPasswordFlow,
        hasVerified,
        isSyncedWithFf,
        usernameMatchesFF,
        reset,
    } = useJdmPasswordReset();

    const onReset = () => {
        setAlerts([]);
        setUserConfirmedUnsync(false);
        reset();
        updateEmail('');
    };

    const onResendForgotPassword = () => {
        forgotPassword(email);
        setAlerts([{ type: AlertType.success, message: 'Code resent successfully.' }]);

        // Clear alerts after 5 seconds
        setTimeout(() => {
            setAlerts([]);
        }, 5000);
    };

    const onVerifyContactCode = (newCode: string) => {
        verifyContactCode(newCode).then(() => setAlerts([]));
    };

    // Determine if we should show the SyncWarning
    const showSyncWarning = hasVerified && isSyncedWithFf && !usernameMatchesFF && !userConfirmedUnsync;

    // Determine if we should show the ResetForm
    const showResetForm = hasVerified && (!isSyncedWithFf || userConfirmedUnsync);

    return (
        <LoginWrap>
            <CenteredContainer>
                <JeppesenHeader show={!isSuccess} showWelcomeHeader={false} onReset={() => {}} />
                <JeppesenForeFlightHeader show={isSuccess} />
                <Alerts alerts={[...alerts, ...passwordAlerts]} />
                <PasswordResetSuccess show={isSuccess} />
                <ForgotPassword
                    show={!hasTriggeredForgotPasswordFlow && !isSuccess}
                    onForgotPassword={forgotPassword}
                    setAlerts={setAlerts}
                    isLoading={isLoading}
                />
                <VerifyCode
                    show={hasTriggeredForgotPasswordFlow && !hasVerified && !isSuccess}
                    code={code}
                    setCode={setCode}
                    isLoading={isLoading}
                    verifyContactCode={onVerifyContactCode}
                    onReset={onReset}
                />
                <SyncWarning
                    show={showSyncWarning && !isSuccess}
                    onContinue={() => setUserConfirmedUnsync(true)}
                    onCancel={onReset}
                    ffResetUrl="/password"
                />
                <ResetForm
                    show={showResetForm && !isSuccess}
                    code={code}
                    setAlerts={setAlerts}
                    isLoading={isLoading}
                    resetPassword={resetPassword}
                    onReset={onReset}
                />
                {!isSuccess && (
                    <ControlBox isCompact={false}>
                        <Stack
                            direction="column"
                            spacing={2}
                            mt="10px"
                            sx={{
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                            }}
                        >
                            {!hasTriggeredForgotPasswordFlow && (
                                <>
                                    <Typography color="gray" fontSize="12px" mb="10px">
                                        Having trouble?{' '}
                                        <a
                                                href="mailto:team@foreflight.com"
                                                style={{
                                                    color: '#4a90e2',
                                                    textDecoration: 'none',
                                                    fontSize: '12px'
                                                }}
                                        >
                                            Contact Support
                                        </a>
                                    </Typography>
                                </>
                            )}
                            {hasTriggeredForgotPasswordFlow && (
                                <Button variant="text" onClick={onResendForgotPassword}>
                                    Resend the Code
                                </Button>
                            )}
                        </Stack>
                    </ControlBox>
                )}
                <Footer />
            </CenteredContainer>
        </LoginWrap>
    );
};

export default PasswordResetJdm;
